import { useState } from "react";
import { getSecureItem, setSecureItem, StorageType } from "../utils/secureStorage";

/**
 * useSecureStorage Hook
 * Uses localStorage with automatic expiration
 * 
 * @param {string} keyName - Storage key
 * @param {*} defaultValue - Default value if key doesn't exist
 * @param {string} storageType - StorageType.LOCAL (default) or StorageType.MEMORY
 */
export const useSecureStorage = (keyName, defaultValue, storageType = StorageType.LOCAL) => {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const value = getSecureItem(keyName, storageType);

      if (value !== null) {
        return value;
      } else {
        setSecureItem(keyName, defaultValue, storageType);
        return defaultValue;
      }
    } catch (err) {
      console.error(`Error initializing secure storage for ${keyName}:`, err);
      return defaultValue;
    }
  });

  const setValue = (newValue) => {
    try {
      setSecureItem(keyName, newValue, storageType);
      setStoredValue(newValue);
    } catch (err) {
      console.error(`Error setting secure storage for ${keyName}:`, err);
    }
  };

  return [storedValue, setValue];
};

/**
 * useLocalStorage - Now uses localStorage with expiration
 */
export const useLocalStorage = (keyName, defaultValue) => {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const value = localStorage.getItem(keyName);
      if (value) {
        return JSON.parse(value);
      } else {
        localStorage.setItem(keyName, JSON.stringify(defaultValue));
        return defaultValue;
      }
    } catch (err) {
      return defaultValue;
    }
  });

  const setValue = (newValue) => {
    try {
      localStorage.setItem(keyName, JSON.stringify(newValue));
    } catch (err) {}
    setStoredValue(newValue);
  };

  return [storedValue, setValue];
};
